import React, { useState, useEffect, useCallback } from "react";
import { Save, View, History } from "lucide-react";
import { Box, IconButton, Tab, ThemeProvider, Tooltip } from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import FloatingItemInformation from "./FloatingItemInformation";
import SectionTab1 from "./SectionTab1";
import SectionTab2 from "./SectionTab2";
import SectionTab3 from "./SectionTab3";
import ConfigCard from "./ConfigCard";
import SummaryTotalCard from "./SummaryTotalCard";
import TimelineTab from "./TimelineTab";
import { HelpOutline } from "@mui/icons-material";
import { stockData } from "./data";
import useApi from "../../hooks/useApi";
import Modal from "../common/CommonModal";
import MasterModal from "./MasterModal";
import { getTodayDate } from "../../utils/constants";
import { useLocation, useNavigate } from "react-router-dom";
import ModeIndicator from "./ModeIndicator";
import {
  DOCUMENT_TYPES,
  fetchDocumentNumber,
  fetchUnitOptionsForItems,
  getDocumentConfig,
  MODIFICATION_TYPES,
  theme,
  tooltipContent,
} from "../config/DocumentConfig";
import ModificationCommentModal from "./ModificationCommentModal";
import SalesInvoiceTable from "./SalesInvoiceTable";
import { calculateRowValues } from "../config/SalesInvoiceTableConfig";
import ItemRequestModal from "./ItemRequestModal";
import checkIfUserHasAccess from "../common/UserCheckAccess";
import { decryptParams } from "../../utils/urlCrypto";

const UnifiedDocument = ({ documentType = DOCUMENT_TYPES.SALES_INVOICE }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { request } = useApi();

  const config = getDocumentConfig(documentType);

  const searchParams = new URLSearchParams(location?.search || "");
  const decoded = decryptParams(searchParams.get("t"));

  const documentNoFromState = decoded?.docNo || location?.state?.docNo || null;
  // console.log("Document number from state", documentNoFromState);
  const modeFromState = decoded?.mode || location?.state?.mode || "new";

  const [adwanceIsOn, setAdwanceIsOn] = useState(false);
  const [autoVatIsOn, setAutoVatIsOn] = useState(true);
  const [recieptSection, setRecieptSection] = useState(true);
  const [receiptToggleManual, setReceiptToggleManual] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [mode, setMode] = useState(modeFromState);
  const [vatAmountManual, setVatAmountManual] = useState(null);
  const [transferInvNo, setTransferInvNo] = useState("");
  const [focusedProductCode, setFocusedProductCode] = useState(null);
  const [isItemRequestModalOpen, setIsItemRequestModalOpen] = useState(false);
  const [qItemsForRequest, setQItemsForRequest] = useState([]);
  const masterCompanyCode = localStorage.getItem("masterCompanyCode") || "-";
  const DEPARTMENT =
    localStorage.getItem("currentSelectedDepartment") || "MAIN";
  const COMPANY_CODE = localStorage?.getItem("companyCode") || "-";
  let API_PATHS = localStorage.getItem("apiPaths")
    ? JSON.parse(localStorage.getItem("apiPaths"))
    : "-";
  const CURRENT_USER = localStorage?.getItem("currentUserName") || "admin";
  const CURRENT_USER_OBJECT = localStorage.getItem("currentUser")
    ? JSON.parse(localStorage.getItem("currentUser"))
    : "";
  const VATPERCENTAGE = CURRENT_USER_OBJECT[0]?.vatp;

  // console.log("User Access", USER_ACCESS);
  const BASE_URL = API_PATHS[0]?.API_CONFIG || "https://cubixweberp.com:213";
  const INVMETHOD = CURRENT_USER_OBJECT[0]?.invMethod;
  const [todayDate, setTodayDate] = useState(getTodayDate);
  const [currentStockInhand, setCurrentStockInhand] = useState(null);
  const [itemPricing, setItemPricing] = useState({ blockPrice: 0, costAvg: 0 });

  const [documentData, setDocumentData] = useState({
    paymentType: config.defaultValues.paymentType,
    documentNo: "",
    date: todayDate,
    isRegistered: false,
    customer: "",
    contactNo: "",
    fax: "",
    trn: "",
    do_no: "",
    lpo_no: "",
    accountDr: "",
    accountCr: "",
    creditLimit: "",
    availLimit: "",
    pdcInhand: "",
    curBal: "",
    areacode: "",
    sale_man: "",
    job: "",
    terms: "",
    duedays: "",
    duedate: "",
    deliveryType: config.defaultValues.deliveryType,
    promDt: "",
    taxableAmt: 0,
    vatAmt: 0,
    bcAmt: 0,
    vatAutoCalc: true,
    appendDO: false,
    partialDO: false,
    directToPrint: false,
    cashMode: false,
    salesman: "",
    notes: "",
    currencyRate: "",
    currency: "",
    remarks: "",
    drivername: "",
    governor: "",
    governorModified: false,
    modificationComment: "",
    vatStatus: VATPERCENTAGE === "0" ? "0% VAT" : "VAT Applied",
    inv_no: "",
    vehicleNo: "",
    primaryErrorField: "",
    nextAction: "",
    voucherType: "",
    jv_num: "",
    tr_refno: "",
    project: "",
    subject: "",
    attention: "",
    delivery: "",
    validity: "",
    deptno: "",
  });

  const [receiptData, setReceiptData] = useState({
    paymentMethods: [{ id: 1, type: "CASH", accountNo: "", amount: "" }],
    commissionExpense: "",
    commissionPayable: "",
    commissionAmount: "",
    commissionAmountChecked: false,
    advanceRvNo: "",
    advanceRvAmount: "",
    totalAmount: "",
  });

  const [masterModalData, setMasterModalData] = useState({
    shipping_type: "Shipping Charges",
    shipping_amt: 0,
  });
  const [commissionAccounts, setCommissionAccounts] = useState({
    expense: "",
    payable: "",
  });
  const [paymentModes, setPaymentModes] = useState([]);
  const [initialRowUnitOptions, setInitialRowUnitOptions] = useState({});
  const [currentLocation, setCurrentLocation] = useState(
    localStorage.getItem("departmentLocation") || "A",
  );
  const [items, setItems] = useState(() => {
    const deptLocation = localStorage.getItem("departmentLocation") || "A";
    return stockData.map((item) => ({
      ...item,
      location: item.location || deptLocation,
    }));
  });
  const [areaCodes, setAreaCodes] = useState([]);
  const [deliveryTabValue, setDeliveryTabValue] = useState("1");
  const [parentTabValue, setParentTabValue] = useState("1");
  const [advancePercentage, setAdvancePercentage] = useState("");
  const [advanceAmount, setAdvanceAmount] = useState(0);
  const [discountAmount, setDiscountAmount] = useState("");
  const [roundOff, setRoundOff] = useState("");
  const [isOtherFieldModalOpen, setIsOtherFieldOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("shipping");
  const [transferModalOpen, setIsTransferModalOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(
    new Date().toLocaleTimeString(),
  );
  const [isModificationModalOpen, setIsModificationModalOpen] = useState(false);

  const [itemInfo, setItemInfo] = useState({
    qtyInhand: 0,
    costAvg: 0,
    lSalesprice: 0,
    lpCost: 0,
    lCSalesprice: 0,
    pSoQty: 36,
    pPoQty: "RRD",
  });

  const [accountNumber, setAccountNumber] = useState(null);
  const [accountData, setAccountData] = useState(null);
  const [accountType, setAccountType] = useState("cash");
  const [glSettings, setGlSettings] = useState([]);

  const { request: requestAccountData } = useApi();

  // Fetch GL Settings for COS method (Sales Invoice & Sales Return only)
  useEffect(() => {
    // if (
    //   INVMETHOD === "COS" &&
    //   (documentType === DOCUMENT_TYPES.SALES_INVOICE ||
    //     documentType === DOCUMENT_TYPES.SALES_RETURN)
    // ) {
    const fetchGLSettings = async () => {
      try {
        const url = `${BASE_URL}/api/CRMGLSettings/${COMPANY_CODE}/SCRN_CODE/SI/-/${DEPARTMENT}/-`;
        const response = await request({
          url,
          method: "GET",
          timeout: 10000,
        });
        setGlSettings(response || []);
      } catch (err) {
        console.error("Failed to fetch GL settings for COS", err);
        setGlSettings([]);
      }
    };
    fetchGLSettings();
    // }
  }, [documentType, BASE_URL, COMPANY_CODE, DEPARTMENT]);

  const transferredData = location?.state?.transferredData || null;
  // console.log("Transferred Data from Distribution Dashboard:", transferredData);
  // Auto-save logic: persist new document drafts to localStorage
  useEffect(() => {
    if (
      mode === "new" &&
      items.some((it) => it.code && it.code.trim() !== "")
    ) {
      const draft = {
        // documentData,
        items,
        // masterModalData,
        // discountAmount,
        // advanceAmount,
        // roundOff,
        // todayDate,
      };
      localStorage.setItem(
        `cubix_draft_${documentType}`,
        JSON.stringify(draft),
      );
    }
  }, [
    items,
    documentData,
    masterModalData,
    discountAmount,
    advanceAmount,
    roundOff,
    todayDate,
    documentType,
    mode,
  ]);

  const handleRetrieveDraft = useCallback(() => {
    const savedDraft = localStorage.getItem(`cubix_draft_${documentType}`);
    if (savedDraft) {
      const draft = JSON.parse(savedDraft);
      const confirmRestore = window.confirm(
        "Found a previously saved draft. Would you like to restore it?",
      );
      if (confirmRestore) {
        // if (draft.documentData) setDocumentData(draft.documentData);
        if (draft.items) setItems(draft.items);
        // if (draft.masterModalData) setMasterModalData(draft.masterModalData);
        // if (draft.discountAmount !== undefined)
        //   setDiscountAmount(draft.discountAmount);
        // if (draft.advanceAmount !== undefined)
        //   setAdvanceAmount(draft.advanceAmount);
        // if (draft.roundOff !== undefined) setRoundOff(draft.roundOff);
        // if (draft.todayDate) setTodayDate(draft.todayDate);
      }
    } else {
      alert("No draft found for this document type.");
    }
  }, [documentType]);

  // Keyboard shortcut listener (Alt + R to restore)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.altKey && e.key === "r") {
        e.preventDefault();
        handleRetrieveDraft();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleRetrieveDraft]);

  // Navigation Guard: Warn user before leaving with unsaved items
  useEffect(() => {
    const hasUnsavedChanges =
      mode === "new" && items.some((it) => it.code && it.code.trim() !== "");

    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue =
          "You have unsaved items in your draft. Are you sure you want to leave?";
        return e.returnValue;
      }
    };

    // Browser-level listener (Refresh, Close Tab)
    window.addEventListener("beforeunload", handleBeforeUnload);

    // Optional: Internal navigation handling would require useBlocker (v6.7+)
    // but we can add a basic confirmation if the user tries to navigate via 'navigate'
    // by wrapping navigation calls if needed. For now, we'll use the platform standard.

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [items, mode]);

  useEffect(() => {
    if (!transferredData?.master) return;

    const master = transferredData.master;

    // Fill header/master fields
    setDocumentData((prev) => ({
      ...prev,
      customer: master.cust_acc || master.lead_account || prev.customer,
      salesman: (master.sale_man || master.lead_salesperson || "").trim(),
      lpo_no: master.lpo_no || master.jv_num || prev.lpo_no,
      do_no: master.do_no || prev.do_no,
      areacode: master.area_code || master.lead_area || prev.areacode,
      duedate: master.due_date ? master.due_date.slice(0, 10) : prev.duedate,
      date: master.so_date ? master.so_date.slice(0, 10) : prev.date,
      paymentType: master.cashcred === "C" ? "cash" : "credit",
      currency: master.fc || master.lead_currencycode || prev.currency,
      currencyRate:
        master.so_fcrate || master.lead_currencyrate || prev.currencyRate,
      remarks: master.comments || prev.remarks,
      jv_num: master.jv_num || prev.jv_num,
      tr_refno: "SORD",
      project: master.insurance,
    }));

    // Discount/account related autofill
    setDiscountAmount(master.so_disc || master.disc_amt || 0);
    setAccountType(master.cashcred === "C" ? "cash" : "credit");
    handleAccountSelect(
      master.cashcred === "C"
        ? master.type_no || master.autoid
        : master.cust_acc || master.lead_account,
      master.cashcred === "C" ? "cash" : "credit",
    );

    // Fill shipping/master modal data if present
    setMasterModalData((prev) => ({
      ...prev,
      shipping_type: master.shipping_type || prev.shipping_type,
      shipping_amt: master.shipping_amt || prev.shipping_amt,
    }));

    // Populate items, if any
    if (
      Array.isArray(transferredData.items) &&
      transferredData.items.length > 0
    ) {
      (async () => {
        try {
          const mappedItems = transferredData.items.map((row, idx) => ({
            id: Date.now() + idx,
            sino: idx + 1,
            code: row.code || row.so_icode || row.do_icode || "",
            description: row.idesc || row.description || "",
            location: row.locn || "A",
            unit: (row.unit || row.Unit || "").trim() || "",
            qty: row.qty || row.so_qty || row.tr_qty || 0,
            unitPrice:
              row.so_cost ||
              row.funit_pric ||
              row.unitPrice ||
              Number(row.unitPrice) ||
              0,
            discount: row.so_disc || row.discount || 0,
            fraction:
              row.fraction != null
                ? String(row.fraction)
                : row.frac != null
                  ? String(row.frac)
                  : "1",
            quotno:
              row.quot_slNo != null ? String(row.quot_slNo) : row.quotno || "",
            cntrl: row.doguid || row.soUid || row.uid || "",
            dono: row.do_no || "0",
          }));

          const initialRowUnitOptions = await fetchUnitOptionsForItems(
            mappedItems,
            BASE_URL,
            COMPANY_CODE,
          );

          setItems(mappedItems);
          setInitialRowUnitOptions(initialRowUnitOptions);
        } catch (err) {
          console.error("Error applying transferred items:", err);
        }
      })();
    }
  }, [transferredData, BASE_URL, COMPANY_CODE]);

  //  transfer from DO when coming from InvoiceGenerationTab
  useEffect(() => {
    const { transferData, shouldFetchTransfer, transferredFrom } =
      location?.state || {};

    if (
      shouldFetchTransfer &&
      transferredFrom &&
      transferData &&
      transferData.length > 0
    ) {
      handleTransfer(transferredFrom, transferData);

      // prevent re-trigger
      navigate(location.pathname, {
        replace: true,
        state: {
          ...location.state,
          shouldFetchTransfer: false,
        },
      });
    }
  }, [
    location?.state?.transferData,
    location?.state?.shouldFetchTransfer,
    location?.state?.transferredFrom,
  ]);

  const fetchDocumentDetail = async (documentNo) => {
    try {
      const url = config.apiEndpoints.detail(
        COMPANY_CODE,
        documentNo,
        DEPARTMENT,
      );
      const fullUrl = `${BASE_URL}${url}`;
      const response = await request({ url: fullUrl, method: "GET" });

      if (response && response.length > 0) {
        const main = response[0];
        console.log(`Fetched ${config.title} Detail:`, main);

        const fieldMap = config.fieldMappings;

        setDocumentData((prev) => ({
          ...prev,
          documentNo: String(main[fieldMap.documentNo]),
          customer: main[fieldMap.customerRef] || main[fieldMap.customerAcc],
          salesman: main.sale_man,
          lpo_no: main.so_ref || main.lpo_no || main.jv_num || main.so_ref,
          do_no: main.do_no,
          areacode: main.area_code,
          duedate: main.due_date,
          terms: main.terms,
          vatStatus: main.deliv_site,
          date: main[fieldMap.documentDate]?.slice(0, 10),
          paymentType: main.cashcred?.trim() === "C" ? "cash" : "credit",
          currency: main.fc,
          currencyRate: main.rate || 1,
          remarks: main.comments || "",
          governor: main.governor,
          inv_no: main.sr_inv_no,
          vehicleNo: main[fieldMap.documentVehicleNo] || main?.vehicleno,
          drivername: main[fieldMap.documentDriverName] || main?.drivername,
          primaryErrorField: main?.Primary_error,
          nextAction: main.NextAction || main.head1 || "",
          voucherType: main.cap || "",
          deliveryType: main.delivType || "",
          jv_num: main.jv_num || main.sr_inv_no || "0",
          job: main.job_code || "",
          tr_refno: main.tr_refno,
          project: main.insurance || "",
          governorModified: true,
          delivery: main.delivery,
          validity: main.validity,
          subject: main.manuf,
          deptno: main?.deptno,
        }));
        setDiscountAmount(main.disc_amt || 0);
        setRoundOff(main.e || 0);
        setTransferInvNo(main?.sr_inv_no);
        setAccountType(main.cashcred?.trim() === "C" ? "cash" : "credit");
        handleAccountSelect(
          main.cashcred?.trim() === "C"
            ? main.type_no || main.autoid
            : main.cust_acc,
          main.cashcred?.trim() === "C" ? "cash" : "credit",
        );

        if (config.features.showReceipt) {
          setReceiptData((prev) => ({
            ...prev,
            rcvd_mode1: main.rcvd_mode1 || "",
            rcvd_mode1_acc: main.rcvd_mode1_acc || "",
            rcvd_mode1_amt: main.rcvd_mode1_amt || 0.0,
            rcvd_mode2: main.rcvd_mode2 || "",
            rcvd_mode2_acc: main.rcvd_mode2_acc || "",
            rcvd_mode2_amt: main.rcvd_mode2_amt || 0.0,
            commissionExpense: main.commi_acc_Dr || "",
            commissionPayable: main.commi_acc_Cr || "",
            commissionAmount: main.commi_amt || 0,
          }));
        }
        if (config.features.showAdvance) {
          const isAdvance = main.awb?.trim() === "ADVANCE";

          const rawAmount = main[fieldMap.documentQty];
          const amount = rawAmount ? parseFloat(rawAmount) : 0;

          const percentage = main.paid_amt ? Number(main.paid_amt) : "";

          setAdwanceIsOn(isAdvance);
          setAdvanceAmount(amount);
          setAdvancePercentage(percentage);

          console.log("Advance Amount fetched:", amount);
        }

        setMasterModalData((prev) => ({
          ...prev,
          shipping_type: main.shipping_type || "Shipping Charges",
          shipping_amt: main.shipping_amt || 0,
        }));

        const items = response.map((row, idx) => ({
          id: idx + 1,
          sino: idx + 1,
          code: row.code || row.do_icode || row.so_icode,
          description: row.idesc,
          location: row.locn || row.do_locn || "A",
          unit: row.unit,
          qty: row.tr_qty2 || row.do_qty || row.so_qty,
          originalQty: row.tr_qty2 || row.do_qty || row.so_qty,
          unitPrice: row.funit_pric || row.do_fprice || row.so_fccost || 0,
          discount: row.disc_amt1,
          fraction: row.fraction != null ? String(row.fraction) : "1",
          "vat%": row.bcdisc,
          itemCost: row.unit_cost || row.unitCost || 0,
          cntrl: row.soGuid || row.soUid || row.doguid || "",
          dono: row.do_no1 || "",
          // "Disc%": row.fdisc_amt1,
          "Discount%": row.disc_amt1,
          bin: row.batch || row.bin,
        }));

        const initialRowUnitOptions = await fetchUnitOptionsForItems(
          items,
          BASE_URL,
          COMPANY_CODE,
        );
        setItems(items);
        setInitialRowUnitOptions(initialRowUnitOptions);
      }
    } catch (err) {
      console.error(`Failed to fetch ${config.title} detail:`, err);
    }
  };

  useEffect(() => {
    if (documentNoFromState && request) {
      fetchDocumentDetail(documentNoFromState);
    } else {
      setDocumentData((prev) => ({
        ...prev,
        date: todayDate,
      }));
    }
  }, [documentNoFromState, documentType]);

  const onPickItems = async (invNo, selectedItemRows = []) => {
    if (!invNo) return;

    const inv = String(invNo).trim();
    if (!inv) return;

    try {
      const url = config.apiEndpoints.invoiceDetail(
        COMPANY_CODE,
        inv,
        DEPARTMENT,
      );
      const fullUrl = `${BASE_URL}${url}`;
      const response = await request({ url: fullUrl, method: "GET" });

      if (response && response.length > 0) {
        const main = response[0];
        const fieldMap = config.fieldMappings;

        setDocumentData((prev) => ({
          ...prev,
          // documentNo: main[fieldMap.documentNo],
          customer: main[fieldMap.customerRef] || main[fieldMap.customerAcc],
          salesman: main.sale_man,
          lpo_no: main.lpo_no || main.so_ref,
          do_no: main.do_no,
          areacode: main.area_code,
          duedate: main.due_date,
          terms: main.terms,
          vatStatus: main.deliv_site,
          date: main[fieldMap.documentDate]?.slice(0, 10),
          paymentType: main.cashcred?.trim() === "C" ? "cash" : "credit",
          currency: main.fc,
          currencyRate: main.rate || 1,
          remarks: main.comments || "",
          governor: main.governor,
          project: main.insurance || "",
          jv_num: main.jv_num,
        }));
        handleAccountSelect(
          main.cashcred?.trim() === "C"
            ? main.type_no || main.autoid
            : main.cust_acc,
          main.cashcred?.trim() === "C" ? "cash" : "credit",
        );

        if (config.features.showReceipt) {
          setReceiptData((prev) => ({
            ...prev,
            rcvd_mode1: main.rcvd_mode1 || "",
            rcvd_mode1_acc: main.rcvd_mode1_acc || "",
            rcvd_mode1_amt: main.rcvd_mode1_amt || 0.0,
            rcvd_mode2: main.rcvd_mode2 || "",
            rcvd_mode2_acc: main.rcvd_mode2_acc || "",
            rcvd_mode2_amt: main.rcvd_mode2_amt || 0.0,
            commissionExpense: main.commi_acc_Dr || "",
            commissionPayable: main.commi_acc_Cr || "",
            commissionAmount: main.commi_amt || 0,
          }));
        }

        if (config.features.showAdvance) {
          const isAdvance = main.awb?.trim() === "ADVANCE";

          const rawAmount = main[fieldMap.documentQty];
          const amount = rawAmount ? parseFloat(rawAmount) : 0;

          const percentage = main.paid_amt ? Number(main.paid_amt) : "";

          setAdwanceIsOn(isAdvance);
          setAdvanceAmount(amount);
          setAdvancePercentage(percentage);

          console.log("Advance Amount fetched:", amount);
        }

        setMasterModalData((prev) => ({
          ...prev,
          shipping_type: main.shipping_type || "Shipping Charges",
          shipping_amt: main.shipping_amt || 0,
        }));
      }
      const mappedItems = (selectedItemRows || []).map((row, idx) => ({
        id: Date.now() + idx,
        sino: idx + 1,
        code: row.code || row.so_icode || row.do_icode || "",
        description: row.idesc || row.description || "",
        location: row.locn || row.location || "A",
        unit: (row.unit || row.Unit || "").trim() || "",
        qty: row.so_qty || row.tr_qty || row.qty || 0,
        unitPrice:
          row.so_cost ||
          row.funit_pric ||
          row.unitPrice ||
          Number(row.unitPrice) ||
          0,
        discount: row.so_disc || row.discount || 0,
        fraction:
          row.fraction != null
            ? String(row.fraction)
            : row.frac != null
              ? String(row.frac)
              : "1",
        // keep any other fields used in your table (quotno, etc.)
        quotno:
          row.quot_slNo != null ? String(row.quot_slNo) : row.quotno || "",
      }));

      // fetch unit options for these mapped items (so unit dropdown shows correct options)
      const unitOptions = await fetchUnitOptionsForItems(
        mappedItems,
        BASE_URL,
        COMPANY_CODE,
      );

      setItems(mappedItems);
      setInitialRowUnitOptions(unitOptions);

      // set the transfer input to show chosen inv
      setTransferInvNo(inv);
      setDocumentData((prev) => {
        return { ...prev, jv_num: inv };
      });

      // close the transfer modal
      setIsTransferModalOpen(false);
    } catch (err) {
      console.error("Error during onPickItems:", err);
      alert("Failed to fetch invoice header or apply selected items.");
    }
  };

  // Import items from parsed Excel rows (expects array of row objects)
  const handleImportExcel = async (rows = []) => {
    if (!rows || rows.length === 0) {
      alert("No rows found in the uploaded file.");
      return;
    }

    try {
      // Normalize keys to lowercase for flexible header names
      const normalized = rows.map((r) => {
        const n = {};
        Object.keys(r || {}).forEach((k) => {
          n[k.toLowerCase().trim()] = r[k];
        });
        return n;
      });

      const first = normalized[0] || {};
      const hasCode =
        "code" in first || "prod_code" in first || "icode" in first;
      const hasDesc =
        "description" in first || "desc" in first || "idesc" in first;
      const hasQty = "qty" in first || "quantity" in first;

      if (!hasCode || !hasDesc || !hasQty) {
        alert(
          "Excel must contain at least these columns (case-insensitive): code, description, qty (optional unit price).",
        );
        return;
      }

      const mappedItems = normalized.map((r, idx) => {
        const code = r.code || r.prod_code || r.icode || "";
        const description = r.description || r.desc || r.idesc || "";
        const qty = Number(r.qty || r.quantity || 0) || 0;
        const unitPrice =
          Number(r["unit price"] || r.unitprice || r.price || 0) || 0;
        const unit = (r.unit || r["unit"] || "").toString().trim();

        return {
          id: Date.now() + idx,
          sino: idx + 1,
          code: String(code).trim(),
          description: String(description).trim(),
          location: "A",
          unit,
          qty,
          unitPrice,
          discount: 0,
          fraction: "1",
        };
      });

      // fetch unit options for these items and set into state
      const initialRowUnitOptions = await fetchUnitOptionsForItems(
        mappedItems,
        BASE_URL,
        COMPANY_CODE,
      );

      setItems(mappedItems);
      setInitialRowUnitOptions(initialRowUnitOptions);

      alert(`Imported ${mappedItems.length} rows from Excel.`);
    } catch (err) {
      console.error("Error importing excel rows", err);
      alert("Import failed. Check console for details.");
    }
  };

  // Fetch payment modes (only for Sales Invoice)
  useEffect(() => {
    if (!config.features.showPaymentModes) return;

    const fetchPaymentModes = async () => {
      try {
        const endpoint = `${BASE_URL}/api/CpaysCount/${COMPANY_CODE}/RECEIPTMODES/${DEPARTMENT}/-`;
        const response = await fetch(endpoint);
        const data = await response.json();
        setPaymentModes(data.sort((a, b) => a.Order - b.Order));
      } catch (error) {
        setPaymentModes([]);
        console.error("Failed to fetch payment modes:", error);
      }
    };
    fetchPaymentModes();
  }, [config.features.showPaymentModes]);

  // Handle transfer based on document type
  const handleTransfer = async (docType, selectedRows) => {
    setIsTransferModalOpen(false);
    console.log("Selected rows from Parent component", selectedRows);
    const docNos = selectedRows
      .map((row) => {
        return (
          row.QUOTNO ||
          row.inv_no ||
          row.ORDNO ||
          row.do_no ||
          row.DONO ||
          row.lead_id ||
          row.Prop_No
        );
      })
      .join(",");
    console.log("Doc number transfered", docNos);
    const masterUrl = `${BASE_URL}${config.apiEndpoints.transfer.master(
      COMPANY_CODE,
      DEPARTMENT,
      docNos,
      docType,
    )}`;
    const detailUrl = `${BASE_URL}${config.apiEndpoints.transfer.detail(
      COMPANY_CODE,
      DEPARTMENT,
      docNos,
      docType,
    )}`;

    try {
      const masterRes = await fetch(masterUrl);
      const masterData = await masterRes.json();
      console.log("Master Transfer Response:", masterData);

      if (masterData && masterData.length > 0) {
        const totalDiscount = masterData?.reduce(
          (acc, row) => acc + (row.so_disc || row.disc_amt || 0),
          0,
        );
        const main = masterData[0];

        setDocumentData((prev) => ({
          ...prev,
          customer: main.cust_acc || main.lead_account,
          salesman: main.sale_man?.trim() || main.lead_salesperson,
          so_ref: main.so_ref,
          areacode: main.area_code || main.lead_area,
          duedate: main.due_date?.slice(0, 10),
          terms: "",
          date: main.so_date?.slice(0, 10),
          paymentType: main.cashcred?.trim() === "C" ? "cash" : "credit",
          currency: main.fc || main.lead_currencycode,
          currencyRate: main.so_fcrate || main.lead_currencyrate || 1,
          remarks: main.comments || "",
          jv_num: docType,
          job: main.job_code || "",
          lpo_no: main.lpo_no,
          governor: main.governor,
          tr_refno: docType,
          project: main.insurance || "",
          lpo_no: main.so_ref || main.lpo_no || "",
          deptno: main?.deptno,
        }));

        setDiscountAmount(totalDiscount);
        setAccountType(main.cashcred?.trim() === "C" ? "cash" : "credit");
        handleAccountSelect(
          main.cashcred?.trim() === "C"
            ? main.type_no || main.autoid
            : main.cust_acc || main.lead_account,
          main.cashcred?.trim() === "C" ? "cash" : "credit",
        );
      }

      const detailRes = await fetch(detailUrl);
      const detailData = await detailRes.json();
      console.log("Detail Transfer Response:", detailData);

      if (detailData && detailData.length > 0) {
        let items = detailData.map((row, idx) => {
          //  sono and quotno based on the docType being transferred
          let sono = "0";
          let quotno = "0";

          if (docType === "SORD") {
            // Transferring FROM Sales Order: set sono to actual SO number, quotno to 0
            sono = row.so_no || "0";
            quotno = "0";
          } else if (docType === "QUOT") {
            // Transferring FROM Quotation: set quotno to actual quote number, sono to 0
            quotno = row.quot_no || row.quot_slNo || "0";
            sono = "0";
          } else if (docType === "DORD") {
            // Transferring FROM Delivery Order: set dono, keep sono and quotno as 0
            sono = "0";
            quotno = "0";
          }

          return {
            id: idx + 1,
            sino: idx + 1,
            code: row.so_icode || row.do_icode || row.prod_code,
            description: row.idesc || row.prod_Description,
            location: row.locn || row.do_locn || "A",
            unit: row.unit?.trim() || row.prod_Unit,
            qty: row.so_qty || row.Do_qty || row.prod_Qty,
            unitPrice: row.so_cost || row.do_fprice || row.prod_Price || 0,
            discount: row.so_disc,
            fraction: row.frac != null ? String(row.frac) : "1",
            cntrl: row.doguid || row.soUid || row.uid || "",
            dono: row.do_no || "0",
            sono,
            quotno,
            costAvg: row.cost_Avg || "",
          };
        });
        // CostAvg price highlighting for all users
        const itemsWithBelowCostFlag = items.map((item) => {
          const code = (item?.code || "").toString().trim();
          if (!code) return item;
          const enteredPrice = Number(item.unitPrice) || 0;
          const costAvgVal = Number(item.costAvg) || 0;
          if (enteredPrice > 0 && costAvgVal > 0 && enteredPrice < costAvgVal) {
            return { ...item, _belowCost: true };
          }
          return item;
        });
        items = itemsWithBelowCostFlag;

        const initialRowUnitOptions = await fetchUnitOptionsForItems(
          items,
          BASE_URL,
          COMPANY_CODE,
        );
        if (documentData.appendDO) {
          setItems((prevItems) => {
            const existingItems = prevItems.filter(
              (item) => item.code && item.code.trim() !== "",
            );
            const nextSino = existingItems.length;
            const newItemsWithIndex = items.map((item, idx) => ({
              ...item,
              sino: nextSino + idx + 1,
              id: Date.now() + idx,
            }));
            return [...existingItems, ...newItemsWithIndex];
          });
        } else {
          setItems(items);
        }
        setInitialRowUnitOptions(initialRowUnitOptions);
      }
    } catch (err) {
      console.error("Error fetching transfer data:", err);
    }
  };

  // Account selection handler
  const handleAccountSelect = (accountNumber, type) => {
    setAccountNumber(accountNumber);
    setAccountType(type);
    console.log("Selected Account:", accountNumber, "Type:", type);
  };

  // Fetch account information
  useEffect(() => {
    if (!accountNumber || !accountType) return;

    const fetchData = async () => {
      try {
        const endpoint =
          accountType === "cash"
            ? `${BASE_URL}/api/CRMAccountInfo/${COMPANY_CODE}/Single_BillInfoWalkin/-/${accountNumber}` //autoid for cash accounts
            : `${BASE_URL}/api/CRMAccountInfo/${COMPANY_CODE}/Single_BillInfo/-/${accountNumber}`;

        const response = await requestAccountData({
          url: endpoint,
          method: "GET",
          timeout: 10000,
        });

        const account = response[0];
        setAccountData(account);
        console.log("Fetched Account Data:", account);

        setDocumentData((prev) => {
          if (prev.governorModified || prev.governor) return prev;
          return {
            ...prev,
            governor: account?.TRN || "",
          };
        });

        console.log(
          "Documentdata after setting account",
          documentData,
          documentData.governorModified,
        );
      } catch (error) {
        console.error("Error fetching account data:", error);
      }
    };

    fetchData();
  }, [accountNumber, accountType]);

  // Fetch area codes
  useEffect(() => {
    const fetchData = async () => {
      try {
        const endpoint = `${BASE_URL}/api/CpaysCount/${COMPANY_CODE}/AREALIST/-/-`;
        const response = await requestAccountData({
          url: endpoint,
          method: "GET",
          timeout: 10000,
        });
        setAreaCodes(response);
      } catch (error) {
        console.error("Error fetching Area codes:", error);
      }
    };
    fetchData();
  }, []);

  const initializeDocument = useCallback(async () => {
    try {
      const { voucherType, documentNo } = await fetchDocumentNumber(
        documentType,
        documentData?.paymentType,
        BASE_URL,
        COMPANY_CODE,
        DEPARTMENT,
      );
      console.log("Document Type in initialize document", documentType);
      console.log("Vouchur typeeeeeeeeeeee", voucherType, documentNo);

      if (documentNo) {
        setDocumentData((prev) => ({
          ...prev,
          documentNo: documentNo,
          voucherType,
        }));
      }
      console.log("Document Type in initialize document", documentType);
      console.log("Voucher Type:", voucherType);
    } catch (err) {
      console.error("Failed to fetch document number:", err);
    }
  }, [
    documentType,
    documentData?.paymentType,
    BASE_URL,
    COMPANY_CODE,
    DEPARTMENT,
  ]);

  useEffect(() => {
    if (mode !== "new") return; // only run for new documents
    initializeDocument();
  }, [mode, initializeDocument]);

  // Auto-show receipt section for cash transactions (Sales Invoice Only)
  useEffect(() => {
    if (!config.features.showReceipt) {
      setRecieptSection(false);
      return;
    }

    if (!receiptToggleManual) {
      const isCash =
        documentData.paymentType === "cash" || documentData.cashMode;
      setRecieptSection(isCash);
    }
  }, [
    documentData.paymentType,
    documentData.cashMode,
    receiptToggleManual,
    config.features.showReceipt,
  ]);

  useEffect(() => {
    setReceiptToggleManual(false);
  }, [documentData.paymentType, documentData.cashMode]);

  // Initialize default rows
  useEffect(() => {
    if (items.length === 0) {
      const defaultRows = Array.from({ length: 3 }, (_, index) => ({
        id: Date.now() + index,
        sino: index + 1,
        code: "",
        description: "",
        location: "",
        unit: "PCS",
        qty: 0,
        unitPrice: 0,
        discount: 0,
      }));
      setItems(defaultRows);
    }
  }, []);

  // Update time
  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(new Date().toLocaleTimeString());
    }, 1000);
    return () => clearInterval(intervalId);
  }, []);

  // Update date
  useEffect(() => {
    setDocumentData((prev) => ({
      ...prev,
      date: todayDate,
    }));
  }, [todayDate]);

  // Tab handlers
  const handleChange = (event, newValue) => {
    setDeliveryTabValue(newValue);
  };

  const handleParentTabChange = (event, newValue) => {
    setParentTabValue(newValue);
  };

  // VAT calculation
  const getVatPercent = (documentData) =>
    documentData.vatStatus === "VAT Applied" ? VATPERCENTAGE : 0;

  const calculateTotals = () => {
    const round = (v) => Math.round(Number(v) * 100) / 100;

    const rows = items.map((item) =>
      calculateRowValues(item, documentData, { items, otherCosts: [] }),
    );

    const rowSubTotal = round(
      rows.reduce((s, r) => s + Number(r.amount || 0), 0),
    );
    const rowVatSum = round(
      rows.reduce((s, r) => s + Number(r.vatAmount || 0), 0),
    );

    const grossRow = rowSubTotal + rowVatSum;

    const globalDiscount = Number(discountAmount) || 0;
    const roundOffValue = Number(roundOff) || 0;

    // BEFORE VAT (Matches print 'subTotal') - roundOff should NOT affect taxable amount
    const taxableBeforeVat = round(rowSubTotal - globalDiscount);

    let vatCalc;
    const vatRate = (Number(VATPERCENTAGE) || 0) / 100;

    vatCalc =
      documentData.vatStatus === "VAT Exempted" ||
      documentData.vatStatus === "0% VAT"
        ? 0
        : round(taxableBeforeVat * vatRate);

    const totalBeforeAdvance = round(
      taxableBeforeVat + vatCalc + roundOffValue,
    );
    const isSalesOrder = documentType === DOCUMENT_TYPES.SALES_ORDER;

    let advance = 0;
    let finalVat = vatCalc;
    let net = 0;
    let balanceContractValue = 0; // New tracking property

    if (isSalesOrder) {
      advance = round(Number(advanceAmount) || 0);
      finalVat = vatCalc;
      net = totalBeforeAdvance;
    } else {
      const advPerc = Number(advancePercentage) || 0;

      if (advPerc > 0) {
        // 1. Advance Subtotal Excl. VAT
        advance = round((taxableBeforeVat * advPerc) / 100);

        // 2. 5% VAT applied specifically to that advance portion
        const advVat =
          autoVatIsOn &&
          documentData.vatStatus !== "VAT Exempted" &&
          documentData.vatStatus !== "0% VAT"
            ? round(advance * vatRate)
            : 0;

        finalVat = advVat;

        // 3. Exact total due right now for this invoice
        net = round(advance + advVat);

        // 4. Contract balance remaining (Inclusive of VAT)
        balanceContractValue = round(totalBeforeAdvance - net);
      } else {
        // Normal invoice
        advance = 0;
        finalVat = vatCalc;
        net = totalBeforeAdvance;
        balanceContractValue = 0;
      }
    }

    const currencyRate = Number(documentData.currencyRate) || 1;
    const bcNet = round(net * currencyRate);

    return {
      taxableAmount: taxableBeforeVat.toFixed(2),
      vatAmount: finalVat.toFixed(2),
      grossAmount: grossRow.toFixed(2),
      totalAmount: totalBeforeAdvance.toFixed(2), // Principal Invoice (Incl. VAT)
      netAmount: net.toFixed(2), // Advance Invoice Amount
      advanceAmount: advance.toFixed(2), // Advance Value Excl. VAT
      balanceAmount: balanceContractValue.toFixed(2), // Balance Contract Value
      bcAmt: bcNet.toFixed(2),
    };
  };

  const totals = calculateTotals();

  // Update advance amount hook
  useEffect(() => {
    if (!config.features.showAdvance) return;

    if (config.documentType === DOCUMENT_TYPES.SALES_ORDER) {
      return;
    }

    const advPerc = Number(advancePercentage) || 0;

    if (advPerc > 0) {
      const taxableAmt = Number(totals.taxableAmount) || 0;

      // Keep this state synchronized with your 'advance' math (Excl. VAT portion)
      const advAmt = ((taxableAmt * advPerc) / 100).toFixed(2);
      setAdvanceAmount(advAmt);
    } else {
      setAdvanceAmount("0.00");
    }
  }, [
    advancePercentage,
    totals.taxableAmount,
    config.features.showAdvance,
    config.documentType,
  ]);
  // Credit limit check helper functions
  async function fetchRefCount(accountId) {
    try {
      const res = await fetch(
        `${BASE_URL}/api/CpaysCount/${COMPANY_CODE}/PENDBILL/${accountId}/-`,
      );
      const data = await res.json();
      return Number(data?.[0]?.RefCount || 0);
    } catch (err) {
      console.error("Error fetching RefCount:", err);
      return 0;
    }
  }

  function isCreditLimitExceeded(accountData, totalAmount, refCount = 0) {
    const creditMethod = String(accountData?.CREDITMETHOD || "").toUpperCase();
    const isCreditCustomer = documentData?.paymentType === "credit";

    const isBlocked = creditMethod === "BLOCKED";
    const isCreditBlock = creditMethod === "CREDIT BLOCK";
    const isOpen = creditMethod === "OPEN";

    const dr = Number(accountData?.debit ?? accountData?.dr ?? 0);
    const cr = Number(accountData?.credit ?? accountData?.cr ?? 0);
    const balance = dr - cr;

    const pendbill = Number(accountData?.discount || 0);
    const duedays = Number(accountData?.duedays || 0);
    const ddays = Number(accountData?.ddays || 0);

    if (!isCreditCustomer) {
      return { blocked: false, exceeded: false, overdue: false, balance };
    }

    if (isOpen) {
      return { blocked: false, exceeded: false, overdue: false, balance };
    }

    if (isBlocked) {
      return { blocked: true, exceeded: false, overdue: false, balance };
    }

    const creditLimit = Number(accountData?.climit || 0);
    const outstanding = dr - cr;
    const availableCredit = creditLimit - outstanding;

    if (isCreditBlock && totalAmount > availableCredit) {
      return {
        blocked: false,
        exceeded: true,
        overdue: false,
        balance: availableCredit,
      };
    }

    if (ddays > 0 && duedays > ddays) {
      return { blocked: false, exceeded: false, overdue: true, balance };
    }

    if (pendbill > 0 && refCount > pendbill) {
      return { blocked: false, exceeded: true, overdue: false, balance };
    }

    return { blocked: false, exceeded: false, overdue: false, balance };
  }

  // Credit limit validation
  useEffect(() => {
    if (!accountData?.CREDITMETHOD) return;

    const runChecks = async () => {
      const totalAmount = Number(totals?.totalAmount) || 0;
      const isCreditCustomer = documentData?.paymentType === "credit";

      if (!isCreditCustomer) return;

      const pendbill = Number(accountData?.discount || 0);
      const refCount =
        pendbill > 0 ? await fetchRefCount(accountData?.account) : 0;

      const { blocked, exceeded, overdue } = isCreditLimitExceeded(
        accountData,
        totalAmount,
        refCount,
      );

      if (blocked) {
        alert("This account is BLOCKED");
        return;
      }

      if (overdue) {
        alert("Customer Bill Overdue");
        return;
      }

      if (exceeded) {
        alert("Credit Limit Exceeded");
        return;
      }
    };

    runChecks();
  }, [totals?.totalAmount, accountData, documentData?.paymentType]);

  const refreshStockDocument = async (
    companyCode,
    documentNumber,
    department,
    fieldMap,
  ) => {
    try {
      const url = `${BASE_URL}/api/CRMStockRefreshDoc/${companyCode}/${fieldMap}/${documentNumber}/${department}`;
      console.log("Calling refreshStockDocument URL:", url);

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const refreshResult = await response.json();
      console.log("Stock refresh response:", refreshResult);

      if (!response.ok) {
        throw new Error(`Stock refresh failed with status: ${response.status}`);
      }

      return refreshResult;
    } catch (error) {
      console.error("Error in refreshStockDocument:", error);
      // We don't necessarily want to throw here to prevent the whole save flow from being "failed"
      // if the document was already saved successfully.
      return { error: error.message };
    }
  };

  // Save handler
  const handleSave = async () => {
    if (!accountData || !accountNumber) {
      alert("Customer is mandatory. Please select a customer before saving.");
      return;
    }

    if (!documentData.salesman || !documentData.salesman.trim()) {
      alert("Salesman is mandatory. Please select a salesman before saving.");
      return;
    }

    const totalAmount = Number(totals?.netAmount) || 0;
    const isCreditCustomer = documentData?.paymentType === "credit";

    // Credit validation for credit customers
    if (isCreditCustomer) {
      const refCount =
        Number(accountData?.pendbill || 0) > 0
          ? await fetchRefCount(accountData?.account)
          : 0;

      const { blocked, exceeded, overdue, balance } = isCreditLimitExceeded(
        accountData,
        totalAmount,
        refCount,
      );

      if (blocked) {
        alert("Account Blocked! Cannot save or add items.");
        return;
      }

      if (overdue) {
        alert("Customer Bill Overdue! Cannot proceed until dues are cleared.");
        return;
      }

      if (exceeded) {
        alert(
          `Credit Limit Exceeded!\nAvailable Balance: ${balance.toFixed(
            2,
          )}\nInvoice Amount: ${totalAmount.toFixed(
            2,
          )}\nYou cannot add more items or save this invoice.`,
        );
        return;
      }
    }

    // Advance validation (Sales Invoice only)
    if (config.features.showAdvance && adwanceIsOn) {
      const advValue = Number(advanceAmount);
      if (!advValue || isNaN(advValue)) {
        alert("Advance value must be set when Advance is ON.");
        return;
      }
    }
    if (documentType === "SALESRETURN") {
      const missing = [];
      const reason = (documentData?.vehicleNo || "").toString().trim();
      const condition = (documentData?.drivername || "").toString().trim();
      const primaryError = (documentData?.primaryErrorField || "")
        .toString()
        .trim();
      const nextAction = (documentData?.nextAction || "").toString().trim();

      if (!reason) missing.push("Reason of Return");
      if (!condition) missing.push("Condition of Part & Packing");
      if (!primaryError) missing.push("Primary Error Source / Cause");
      if (!nextAction) missing.push("Next Action");

      if (missing.length > 0) {
        alert(
          "Please fill the following required fields for Sales Return:\n\n" +
            missing.join("\n"),
        );
        return;
      }
    }
    let itemsToSave = items.map((item) =>
      calculateRowValues(item, documentData, { items, otherCosts: [] }),
    );

    // --- QTY VALIDATION: Prevent saving with empty or zero qty ---
    const isDordTransfer = documentData?.jv_num === "DORD";
    const itemsWithEmptyQty = itemsToSave.filter((item) => {
      const code = (item?.code || "").toString().trim();
      if (code === "") return false; // skip empty rows
      const qty = parseFloat(item.qty);
      return (
        isNaN(qty) ||
        qty === 0 ||
        item.qty === "" ||
        item.qty === null ||
        item.qty === undefined
      );
    });

    if (itemsWithEmptyQty.length > 0 && !isDordTransfer) {
      const rowNumbers = itemsWithEmptyQty
        .map((item) => item.sino || itemsToSave.indexOf(item) + 1)
        .join(", ");
      alert(
        `Please fill in the Qty field for row(s): ${rowNumbers}. Qty cannot be empty or zero.`,
      );
      return;
    }
    // --- BELOW COST VALIDATION: Check for items highlighted as below cost ---
    const belowCostItems = itemsToSave.filter((item) => item._belowCost);
    if (belowCostItems.length > 0) {
      const itemCodes = belowCostItems.map((item) => item.code).join(", ");
      if (checkIfUserHasAccess("MN")) {
        // User has NO MN access → hard block
        alert(
          `Below cost items found: ${itemCodes}\n\nPlease check the highlighted rows before saving.`,
        );
        return;
      } else {
        // User HAS MN access → warning confirm, allow proceed
        const confirmed = window.confirm(
          `Warning: Below cost items found: ${itemCodes}\n\nDo you want to continue saving?`,
        );
        if (!confirmed) return;
      }
    }

    // --- STOCK CHECK VALIDATION (Sales Invoice Only) ---
    if (documentType === DOCUMENT_TYPES.SALES_INVOICE) {
      try {
        const checkPayload = itemsToSave
          .filter((it) => (it?.code || "").toString().trim() !== "")
          .map((it) => ({
            cmpcode: COMPANY_CODE,
            code: String(it.code).trim(),
          }));

        if (checkPayload.length > 0) {
          const response = await fetch(`${BASE_URL}/api/StockCheck`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(checkPayload),
          });

          if (!response.ok) {
            throw new Error("Stock verification API failed.");
          }

          const data = await response.json();

          if (data.notMatchingItems && data.notMatchingItems.length > 0) {
            const missingCodes = data.notMatchingItems
              .map((it) => it.code)
              .join(", ");
            alert(
              `The following items are not in stock or not found:\n${missingCodes}\n\nPlease remove or correct these items before saving.`,
            );
            return;
          }
        }
      } catch (err) {
        console.error("Stock check failed:", err);
        alert("Stock verification failed. Please check your connection.");
        return;
      }
    }

    // Validation: prevent saving Sales Orders that include item codes ending with "-Q"
    if (
      documentType === DOCUMENT_TYPES.SALES_ORDER &&
      masterCompanyCode?.toUpperCase() === "SUNRISEGROUP"
    ) {
      const qItems = itemsToSave.filter((it) => {
        const code = (it?.code || "").toString().trim();
        return code !== "" && code.toUpperCase().endsWith("-Q");
      });

      if (qItems.length > 0) {
        const shouldRequest = window.confirm(
          `Found ${qItems.length} item(s) ending with "-Q". These items require customer approval.\n\n` +
            "Would you like to submit an item request?\n\n" +
            "• Click OK to create a request\n" +
            "• Click Cancel to remove these items and continue",
        );

        if (shouldRequest) {
          setQItemsForRequest(qItems);
          setIsItemRequestModalOpen(true);
          return;
        } else {
          // 2. Filter the local variable immediately
          itemsToSave = itemsToSave.filter((it) => {
            const code = (it?.code || "").toString().trim();
            return code === "" || !code.toUpperCase().endsWith("-Q");
          });

          // Still update state so the UI reflects the removal after the save
          setItems(itemsToSave);

          console.log(`Removed ${qItems.length} item(s) ending with "-Q"`);

          const hasValidItems = itemsToSave.some((it) => {
            const code = (it?.code || "").toString().trim();
            return code !== "";
          });

          if (!hasValidItems) {
            alert("No valid items to save after removing -Q items.");
            return;
          }
        }
      }
    }

    // Block save for all documents except SALES_ORDER when items contain -Q codes
    if (
      documentType !== DOCUMENT_TYPES.SALES_ORDER &&
      masterCompanyCode?.toUpperCase() === "SUNRISEGROUP"
    ) {
      const qItems = itemsToSave.filter((it) => {
        const code = (it?.code || "").toString().trim().toUpperCase();
        return code.endsWith("-Q");
      });
      if (qItems.length > 0) {
        alert(
          "The following item code(s) end with '-Q' or '-q' and cannot be saved:\n\n" +
            qItems.map((it) => it.code).join("\n"),
        );
        return;
      }
    }
    const fieldMap = config.fieldMappings;
    const totalDocumentCost = itemsToSave
      .filter((item) => item.code && item.code.trim() !== "")
      .reduce(
        (sum, item) =>
          sum +
          Number(item.qty || 0) *
            Number(item.itemCost || item.unitCost || item["Unit Cost"] || 0),
        0,
      )
      .toString();

    const documentRows = itemsToSave
      .filter((item) => item.code && item.code.trim() !== "")
      .map((item, idx) => {
        const basePayload = {
          cmpcode: COMPANY_CODE,
          modeop: documentNoFromState ? "EDIT" : "SAVE",
          cashcustomer: documentData.paymentType === "cash" ? "yes" : "no",
          [fieldMap.documentNo]: documentData.documentNo || "1001",
          [fieldMap.customerAcc]:
            documentData?.paymentType === "cash"
              ? glSettings?.find((g) => g.Scrn_code === "SI-CA-D")?.ACCOUNT ||
                ""
              : accountData?.ACCOUNT ||
                // accountData?.AutoId ||
                // accountData?.cracc ||
                accountData?.account ||
                "0",
          [fieldMap.documentTotal]: totals.netAmount?.toString() || "0",
          [fieldMap.documentCost]: totalDocumentCost,

          // Common fields
          [fieldMap.documentJvNo || "jv_num"]: documentData.jv_num || "0",
          comments: documentData.remarks || "",
          disc_amt: Number(discountAmount) || "0",
          sale_man: documentData.salesman || "",
          [fieldMap.documentLpoNo || "lpo_no"]: documentData.lpo_no || "",
          [fieldMap.documentInvno]: documentData.inv_no || "0",
          due_date: documentData.duedate || todayDate,
          area_code: documentData.areacode || "",
          type: documentData.deliveryType || "",
          do_data: "",
          q_no: "0",
          inv_type: "",
          user_id: CURRENT_USER,
          fdisc_amt: "0",
          costc: "",
          job_code: documentData.job || "",
          [fieldMap.documentFctota]: "0",
          fc: documentData.currency || "AED",
          awb: adwanceIsOn ? "ADVANCE" : "",
          blno: "",
          pkg: "",
          rate2: "0",
          rate: documentData.currencyRate || "1",
          invcurr: "",
          supl_code: "",
          status: documentData.status || "",
          tr_refno: documentData.tr_refno || "",
          payment: documentData.paymentType || "",
          manuf: documentData.subject || "",
          origin: "",
          shipment: "",
          delivery: documentData.delivery,
          packing: "",
          netwt: "",
          grosswt: "",
          insurance: documentData.project || "",
          custom: "",
          validity: documentData.validity,
          foot1: "",
          frdet:
            INVMETHOD === "COS" &&
            (documentType === DOCUMENT_TYPES.SALES_INVOICE ||
              documentType === DOCUMENT_TYPES.SALES_RETURN)
              ? glSettings.find((g) => g.Scrn_code === "SI-COG-D")?.ACCOUNT ||
                ""
              : "",
          fruprice: "0",
          framt: "0",
          fob: "",
          repeat:
            INVMETHOD === "COS" &&
            (documentType === DOCUMENT_TYPES.SALES_INVOICE ||
              documentType === DOCUMENT_TYPES.SALES_RETURN)
              ? glSettings.find((g) => g.Scrn_code === "SI-STK-C")?.ACCOUNT ||
                ""
              : "",
          comm: "0",
          date_paid: `${todayDate}`,
          commdue: "0",
          comm_paid: "0",
          commp: "0",
          type_no:
            documentData.paymentType === "cash"
              ? String(accountNumber || "0")
              : "0",
          paid_amt: advancePercentage || "0", // advance percentage
          client_pd: documentData.vatStatus || "",
          tel: accountData?.phone || "",
          custref: accountData?.accdesc || "",
          [fieldMap.customerRef]: accountData?.accdesc || "",
          terms: documentData.terms || "",
          deptno: documentData?.deptno || DEPARTMENT,
          month: "",
          cashcred: "",
          [fieldMap.documentDate]: documentData.date || todayDate,
          [fieldMap.documentSono]: documentData.so_no || "0",
          sys_date: todayDate,
          comm_pd_dt: todayDate,
          pump: "",
          governor: documentData.governor || "",
          feedpump: "",
          injector: "",
          starter: documentData.starter || "",
          enginetype: documentData.enginetype || "CASH",
          date_prom: documentData.promDt || todayDate,
          date_delvd: documentData.date || todayDate,
          [fieldMap.documentQty]: String(advanceAmount || 0), //advance amount
          rcvd_amt: "0",
          time: "",
          lab_charge: "0",
          oth_charge: 0,
          s:
            documentType === DOCUMENT_TYPES.SALES_INVOICE ||
            documentType === DOCUMENT_TYPES.SALES_RETURN
              ? INVMETHOD
              : "",
          m: "0",
          d: "0",
          e: roundOff?.toString() || "0",
          w: String(totals?.vatAmount || 0),
          wa: autoVatIsOn ? "0" : "1",
          pgroup: "",
          upd: "",
          [fieldMap.altDateKey]: documentData.date || todayDate,
          lpo_date: documentData.date || todayDate,
          [fieldMap.documentVehicleNo || "vehicleno"]:
            documentData.vehicleNo || "",
          [fieldMap.documentDriverName || "drivername"]:
            documentData.drivername || "",
          [fieldMap.documentDelivSite || "deliv_site"]:
            documentData.primaryErrorField || documentData.vatStatus || "",
          [fieldMap.documentNextAction || "head1"]: documentData.nextAction,
          cap: documentData.voucherType || "",
          salesacc: "",
          salesAccDesc: "", //Vat account - passing from BACKEND

          // Item-specific fields
          Slno: idx + 1,
          code: (item.code || "").toString().trim(),
          description: item.description || "",
          locn: item.location,
          unit: item.unit || "",
          qty: item.qty?.toString() || "",
          "Unit Price": item.unitPrice?.toString() || "0",
          "Disc%": Number(item.discount) || Number(item["Discount%"]) || "0",
          Ldisc_amt: item.DiscountAmount?.toString() || "0",
          Amount: (
            (Number(item.qty) || 0) * (Number(item.unitPrice) || 0) -
            ((Number(item.qty) || 0) *
              (Number(item.unitPrice) || 0) *
              (Number(item.discount) || 0)) /
              100
          ).toString(),
          x: item.x || "",
          cntrl: item.cntrl || "",
          Fraction: item.fraction || "1",
          "vat%": item.vatPercent?.toString() || VATPERCENTAGE,
          "Unit Cost":
            item.itemCost?.toString() ||
            item.unitCost?.toString() ||
            item["Unit Cost"]?.toString() ||
            "0",
          dono: item.dono || "0",
          sono: item.sono || "0",
          [fieldMap.documentQuotno]: item.quotno || "0",
          Avlqty: item.Avlqty?.toString() || "0",
          Vatamt: item.Vatamt?.toString() || "0",
          BDiscsplit: item.BDiscsplit?.toString() || "0",
          total: item.total?.toString() || "0",
          oem: item.oem || "",
          Bin: item.bin || item.Bin || "",
        };

        //  receipt fields only for Sales Invoice
        basePayload.rcvd_mode1 = receiptData.rcvd_mode1 || "";
        basePayload.rcvd_mode1_acc = receiptData.rcvd_mode1_acc || "";
        basePayload.rcvd_mode1_amt = receiptData.rcvd_mode1_amt || 0.0;
        basePayload.rcvd_mode2 = receiptData.rcvd_mode2 || "";
        basePayload.rcvd_mode2_acc = receiptData.rcvd_mode2_acc || "";
        basePayload.rcvd_mode2_amt = receiptData.rcvd_mode2_amt || 0.0;

        // if (config.features.showCommission) {
        basePayload.commi_acc_Dr = receiptData.commissionExpense || "";
        basePayload.commi_acc_Cr = receiptData.commissionPayable || "";
        basePayload.commi_amt = receiptData.commissionAmount || 0;
        basePayload.oth_charge = receiptData.commissionAmount || 0;
        // }
        // ===  shipping fields ===
        basePayload.shipping_type =
          masterModalData.shipping_type || "Shipping Charges";
        basePayload.shipping_amt = masterModalData.shipping_amt || 0;
        return basePayload;
      });

    console.log(`${config.title} Rows:`, documentRows);
    // return

    try {
      const response = await fetch(`${BASE_URL}${config.apiEndpoints.save}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(documentRows),
      });

      const result = await response.json();
      console.log("Response from API:", result);

      if (response?.status === 200) {
        // Determine document number field based on type
        const docNoField =
          documentType === DOCUMENT_TYPES.SALES_INVOICE
            ? "invoiceNo"
            : documentType === DOCUMENT_TYPES.SALES_ORDER
              ? "soNo"
              : documentType === DOCUMENT_TYPES.QUOTATION
                ? "qoNo"
                : documentType === DOCUMENT_TYPES.SALES_RETURN
                  ? "srno"
                  : "doNo";
        const savedDocNo =
          mode === "edit" || documentNoFromState
            ? documentData.documentNo
            : result?.[docNoField] ||
              result?.invoiceNo ||
              result?.soNo ||
              result?.qoNo ||
              result?.srno ||
              result?.doNo;

        alert(`${config.title} no: ${savedDocNo} saved successfully.`);

        // Clear the draft after successful save
        localStorage.removeItem(`cubix_draft_${documentType}`);

        const refreshType = config.fieldMappings.refreshType;
        if (refreshType && savedDocNo) {
          console.log(
            `Triggering stock refresh for ${refreshType}: ${savedDocNo}`,
          );
          await refreshStockDocument(
            COMPANY_CODE,
            savedDocNo,
            DEPARTMENT,
            refreshType,
          );
        }

        const listRoute =
          documentType === DOCUMENT_TYPES.SALES_INVOICE
            ? "/invoice_list"
            : documentType === DOCUMENT_TYPES.SALES_ORDER
              ? "/sales_order_list"
              : documentType === DOCUMENT_TYPES.QUOTATION
                ? "/quotationList"
                : documentType === DOCUMENT_TYPES.SALES_RETURN
                  ? "/salesreturnList"
                  : "/document_list";

        navigate(listRoute);

        clearAllFields();
        initializeDocument();
      } else {
        alert(`Failed to save ${config.title}.`);
      }
    } catch (error) {
      console.error(`Error saving ${config.title}:`, error);
      alert(`Error saving ${config.title}.`);
    }
  };
  const handleItemRequestSubmit = async (requestedItems) => {
    try {
      const requestPayload = requestedItems.map((item) => ({
        cmpcode: COMPANY_CODE,
        mode: "ENTRY",
        code: item.code,
        description: item.description,
        unit: item.unit || "",
        soUid: item.cntrl || "",
        maincode: "",
        status: "REQUESTED",
        req_date: todayDate,
        Req_User: CURRENT_USER,
      }));

      console.log("Submitting Item Request:", requestPayload);

      const response = await fetch(`${BASE_URL}/api/CRMItemCreationRequest`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestPayload),
      });

      if (response.ok) {
        alert(`Item request submitted successfully!`);

        // Calculate the remaining items for the Sales Order
        const filteredItems = items.filter((it) => {
          const code = (it?.code || "").toString().trim();
          // Keep items that DON'T end in -Q
          return code !== "" && !code.toUpperCase().endsWith("-Q");
        });

        // Update the UI
        setItems(filteredItems);
        setIsItemRequestModalOpen(false);
        setQItemsForRequest([]);

        // Check if there are any valid items left to save as an actual Sales Order
        if (filteredItems.length > 0) {
          // Pass the filtered items DIRECTLY to handleSave
          handleSave(filteredItems);
        } else {
          // This alert is correct if ONLY -Q items were in the grid
          alert(
            "All items were -Q items and have been sent for request. No items left for Sales Order.",
          );
        }
      } else {
        const errorText = await response.text();
        alert(`Failed to submit item request: ${errorText}`);
      }
    } catch (error) {
      console.error("Error submitting item request:", error);
      alert("Error submitting item request. Please try again.");
    }
  };
  // Clear all fields
  const clearAllFields = () => {
    setDocumentData({
      paymentType: config.defaultValues.paymentType,
      documentNo: config.defaultValues.documentNo,
      date: todayDate,
      isRegistered: false,
      customer: "",
      contactNo: "",
      fax: "",
      trn: "",
      do_no: "",
      lpo_no: "",
      accountDr: "",
      accountCr: "",
      creditLimit: "",
      availLimit: "",
      pdcInhand: "",
      curBal: "",
      areacode: "",
      sale_man: "",
      job: "",
      terms: "",
      duedays: "",
      duedate: "",
      deliveryType: config.defaultValues.deliveryType,
      promDt: "",
      taxableAmt: 0,
      vatAmt: 0,
      bcAmt: 0,
      vatAutoCalc: true,
      appendDO: false,
      partialDO: false,
      directToPrint: false,
      cashMode: false,
      salesman: "",
      notes: "",
      currencyRate: "",
      currency: "",
      remarks: "",
      drivername: "",
    });

    if (config.features.showReceipt) {
      setReceiptData({
        paymentMethods: [{ id: 1, type: "CASH", accountNo: "", amount: "" }],
        commissionExpense: "",
        commissionPayable: "",
        commissionAmount: "",
        commissionAmountChecked: false,
        advanceRvNo: "",
        advanceRvAmount: "",
        totalAmount: "",
        rcvd_mode1: "",
        rcvd_mode1_acc: "",
        rcvd_mode1_amt: 0.0,
        rcvd_mode2: "",
        rcvd_mode2_acc: "",
        rcvd_mode2_amt: 0.0,
      });
    }

    const defaultRows = Array.from({ length: 1 }, (_, index) => ({
      id: Date.now() + index,
      sino: index + 1,
      code: "",
      description: "",
      location: "",
      unit: "",
      qty: 0,
      unitPrice: 0,
      discount: 0,
    }));
    setItems(defaultRows);

    setAccountNumber(null);
    setAccountData(null);
    setAccountType("cash");
    setAdwanceIsOn(false);
    setAutoVatIsOn(false);
    setShowSidebar(false);

    setItemInfo({
      qtyInhand: 0,
      costAvg: 0,
      lSalesprice: 0,
      lpCost: 0,
      lCSalesprice: 0,
      pSoQty: 36,
      pPoQty: "RRD",
    });

    setParentTabValue("1");
    setDeliveryTabValue("1");
    setMode("new");
    setTodayDate(getTodayDate);
    console.log("All fields cleared successfully");
  };

  const handlePrint = () => {
    console.log(`Printing ${config.title}:`, { documentData, items });
    // alert(`${config.title} sent to printer!`);
    const listRoute =
      documentType === DOCUMENT_TYPES.SALES_INVOICE
        ? "/invoice_list"
        : documentType === DOCUMENT_TYPES.SALES_ORDER
          ? "/sales_order_list"
          : documentType === DOCUMENT_TYPES.QUOTATION
            ? "/quotationList"
            : documentType === DOCUMENT_TYPES.SALES_RETURN
              ? "/salesreturnList"
              : documentType === DOCUMENT_TYPES.DELIVERY_ORDER
                ? "/document_list"
                : "/document_list";

    navigate(listRoute);
  };

  const handleOpenModal = () => setIsOtherFieldOpen(true);
  const handleCloseModal = () => setIsOtherFieldOpen(false);

  const handleReceiptDataChange = (updatedData) => {
    setReceiptData(updatedData);
  };

  return (
    <div className="w-full relative">
      <div className="flex items-center justify-between lg:px-10 px-2 mb-2 gap-2">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold">{config.title}</h1>
        </div>

        <Tooltip title={tooltipContent} sx={{ margin: 0, padding: 0 }}>
          <IconButton>
            <HelpOutline
              className="text-blue-600 bg-white rounded-full"
              fontSize="medium"
            />
          </IconButton>
        </Tooltip>
      </div>

      <ModeIndicator mode={mode} documentType={documentType} />

      <div className="w-full rounded-lg">
        <div className="lg:px-10">
          <div className="bg-gray-50 border lg:p-3 rounded-lg shadow pb-6">
            <ThemeProvider theme={theme}>
              <TabContext
                value={parentTabValue}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
              >
                <Box
                  sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <TabList onChange={handleParentTabChange} aria-label="">
                    <Tab label="General" value="1" sx={{ padding: 0 }} />
                    <Tab label="Others" value="2" sx={{ padding: 0 }} />
                    <Tab label="Delivery" value="3" sx={{ padding: 0 }} />
                    <Tab label="Timeline" value="4" sx={{ padding: 0 }} />
                  </TabList>
                  <div className="lg:flex hidden items-center gap-3">
                    <button
                      onClick={handleRetrieveDraft}
                      title="Restore last unsaved draft (Alt+R)"
                      className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-white text-sm font-medium rounded-lg shadow hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-400 transition active:scale-95"
                    >
                      <History className="w-4 h-4" />
                      Restore Draft (Alt + R)
                    </button>
                    <button
                      onClick={handlePrint}
                      className="flex items-center gap-2 px-5 py-2.5 border border-indigo-600 text-indigo-600 text-sm font-medium rounded-lg shadow hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-400 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <View className="w-4 h-4" />
                      View
                    </button>
                    <button
                      onClick={() => {
                        if (mode === "edit") {
                          setIsModificationModalOpen(true);
                        } else {
                          handleSave();
                        }
                      }}
                      disabled={mode === "view"}
                      title={mode === "view" ? "Disabled in view mode" : ""}
                      className="flex items-center gap-2 px-5 py-2.5 bg-green-500 text-white text-sm font-medium rounded-lg shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Save className="w-4 h-4" />
                      {mode === "edit" ? "Update" : "Save"}
                    </button>
                  </div>
                </Box>
                <TabPanel value="1" sx={{ p: 0, paddingX: 2 }}>
                  <SectionTab1
                    invoiceData={documentData}
                    setInvoiceData={setDocumentData}
                    todayDate={todayDate}
                    setTodayDate={setTodayDate}
                    currentTime={currentTime}
                    setCurrentTime={setCurrentTime}
                    totals={totals}
                    setRecieptSection={setRecieptSection}
                    onAccountSelect={handleAccountSelect}
                    accountData={accountData}
                    setAccountData={setAccountData}
                    setAccountNumber={setAccountNumber}
                    areaCodes={areaCodes}
                    invoiceNoFromState={documentNoFromState}
                    documentType={documentType}
                    config={config}
                    onRestoreDraft={handleRetrieveDraft}
                    BASE_URL={BASE_URL}
                    COMPANY_CODE={COMPANY_CODE}
                  />
                </TabPanel>
                <TabPanel value="2" sx={{ p: 1 }}>
                  <SectionTab2
                    invoiceData={documentData}
                    setInvoiceData={setDocumentData}
                    theme={theme}
                    deliveryTabValue={deliveryTabValue}
                    handleChange={handleChange}
                    accountData={accountData}
                    areaCodes={areaCodes}
                  />
                </TabPanel>
                <TabPanel value="3" sx={{ p: 0 }}>
                  <SectionTab3
                    invoiceData={documentData}
                    setInvoiceData={setDocumentData}
                    todayDate={todayDate}
                    accountData={accountData}
                  />
                </TabPanel>
                <TabPanel value="4" sx={{ p: 1 }}>
                  <TimelineTab
                    BASE_URL={BASE_URL}
                    DEPARTMENT={DEPARTMENT}
                    modificationType={MODIFICATION_TYPES[documentType] || null}
                    invoiceId={documentNoFromState}
                    COMPANY_CODE={COMPANY_CODE}
                  />
                </TabPanel>
              </TabContext>
            </ThemeProvider>
          </div>
        </div>

        <div className="mt-5 overflow-x-auto max-w-full">
          <SalesInvoiceTable
            items={items}
            onItemsChange={setItems}
            setShowSidebar={setShowSidebar}
            setItemInfo={setItemInfo}
            setCurrentLocation={setCurrentLocation}
            currentLocation={currentLocation}
            extraFields={[]}
            calculateRowValues={calculateRowValues}
            invoiceData={documentData}
            getVatPercent={getVatPercent}
            initialRowUnitOptions={initialRowUnitOptions}
            setFocusedProductCode={setFocusedProductCode}
            currentStockInhand={currentStockInhand}
            mode={mode}
            documentType={documentType}
            itemPricing={itemPricing}
          />
          {checkIfUserHasAccess("FT") && (
            <FloatingItemInformation
              showSidebar={showSidebar}
              currentLocation={currentLocation}
              companyCode={COMPANY_CODE}
              API_BASE_URL={BASE_URL}
              productCode={focusedProductCode}
              department={DEPARTMENT}
              onStockInhandUpdate={setCurrentStockInhand}
              onItemInfoUpdate={setItemPricing}
              onClose={() => setShowSidebar(false)}
            />
          )}
        </div>

        <div className="w-full lg:px-10 p-1 mt-2">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
            <ConfigCard
              invoiceData={documentData}
              setAutoVatIsOn={setAutoVatIsOn}
              setInvoiceData={setDocumentData}
              autoVatIsOn={autoVatIsOn}
              invoiceNoFromState={documentNoFromState}
              accountData={accountData}
              onTransfer={handleTransfer}
              isModalOpen={transferModalOpen}
              setIsModalOpen={setIsTransferModalOpen}
              documentType={documentType}
              config={config}
              transferInvNo={transferInvNo}
              setTransferInvNo={setTransferInvNo}
              fetchDocumentDetail={fetchDocumentDetail}
              onPickItems={onPickItems}
              onImportExcel={handleImportExcel}
              glSettings={glSettings}
            />

            <SummaryTotalCard
              totals={totals}
              setAdwanceIsOn={setAdwanceIsOn}
              setInvoiceData={setDocumentData}
              invoiceData={documentData}
              adwanceIsOn={adwanceIsOn}
              handlePrint={handlePrint}
              handleSave={handleSave}
              setRecieptSection={setRecieptSection}
              recieptSection={recieptSection}
              receiptData={receiptData}
              handleReceiptDataChange={handleReceiptDataChange}
              paymentModes={paymentModes}
              onOpenModal={handleOpenModal}
              advancePercentage={advancePercentage}
              setAdvancePercentage={setAdvancePercentage}
              advanceAmount={advanceAmount}
              roundOff={roundOff}
              setRoundOff={setRoundOff}
              discountAmount={discountAmount}
              setDiscountAmount={setDiscountAmount}
              setReceiptToggleManual={setReceiptToggleManual}
              mode={mode}
              autoVatIsOn={autoVatIsOn}
              setVatAmountManual={setVatAmountManual}
              BASE_URL={BASE_URL}
              DEPARTMENT={DEPARTMENT}
              setCommissionAccounts={setCommissionAccounts}
              config={config}
              setIsModificationModalOpen={setIsModificationModalOpen}
              showAdwance={config.features.showAdvance}
              setAdvanceAmount={setAdvanceAmount}
            />
          </div>
        </div>
      </div>

      <Modal
        isOpen={isOtherFieldModalOpen}
        onClose={handleCloseModal}
        size={"xl"}
        header={true}
        title={"Master Details"}
      >
        <MasterModal
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          masterModalData={masterModalData}
          SetMasterModalData={setMasterModalData}
          closeModal={handleCloseModal}
        />
      </Modal>
      <Modal
        isOpen={isModificationModalOpen}
        onClose={() => setIsModificationModalOpen(false)}
        size="md"
        header={true}
        title="Update Confirmation"
        showFullScreen={false}
      >
        <ModificationCommentModal
          isOpen={isModificationModalOpen}
          onClose={() => setIsModificationModalOpen(false)}
          handleSave={handleSave}
          documentData={documentData}
          documentType={documentType}
          COMPANY_CODE={COMPANY_CODE}
          DEPARTMENT={DEPARTMENT}
          CURRENT_USER={CURRENT_USER}
          BASE_URL={BASE_URL}
          setIsModificationModalOpen={setIsModificationModalOpen}
        />
      </Modal>
      <Modal
        isOpen={isItemRequestModalOpen}
        onClose={() => setIsItemRequestModalOpen(false)}
        size="xxl"
        header={true}
        title="Item Request Modal"
        showFullScreen={false}
      >
        <ItemRequestModal
          isOpen={isItemRequestModalOpen}
          onClose={() => setIsItemRequestModalOpen(false)}
          qItems={qItemsForRequest}
          onSubmitRequest={handleItemRequestSubmit}
          documentData={documentData}
          accountData={accountData}
        />
      </Modal>
    </div>
  );
};

export default UnifiedDocument;
